import React, { useEffect, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ReportFileExports from 'components/Reports/FileExports';
import ApiHelper from 'utils/apiHelper';
import { API_URL, gridSpacing } from 'utils/constants';
import DeviceTypeDevicesDialog from './DeviceTypeDevicesDialog';
import ReportVisualization from 'components/Reports/ReportVisualization';

const initialState = {
    os: { values: [], labels: [] },
    type: { values: [], labels: [] }
};

const ReportsDeviceType = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [groupId, setGroupId] = useState(0);
    const [policyId, setPolicyId] = useState(0);
    const [groupsData, setGroupsData] = useState([]);
    const [policiesData, setPoliciesData] = useState([]);
    const [deviceTypeData, setDeviceTypeData] = useState(initialState);
    const [showDevicesByOs, setShowDevicesByOs] = useState(false);
    const [showDevicesByType, setShowDevicesByType] = useState(false);
    const [type, setType] = useState('os');
    const [osLabel, setOSLabel] = useState('');
    const [typeLabel, setTypeLabel] = useState('');
    const [viewData, setViewData] = useState([]);
    const [viewId, setViewId] = useState(0);
    const [viewStatements, setViewStatements] = useState([]);
    const [filters, setFilters] = useState({});
    const [selectedStatements, setSelectedStatements] = useState([]);

    const initialViewData = async () => {
        setLoading(true);
        try {
            // Get views list and statements
            const [views, viewStatements] = await Promise.all([
                ApiHelper.get('/api/management/views/'),
                ApiHelper.get('/api/management/get_view_statements/')
            ]);

            // Only proceed if we have views
            if (views.data.res.length > 0) {
                // Update views list and statements
                setViewData(views.data.res);
                setViewStatements(viewStatements.data);
            }
        } catch (err) {
            if (err.response?.status !== 401) {
                toast.error('Failed to fetch views data');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        initialViewData();
        getGroups();
        getPolicies();
    }, []);

    const getGroups = () => {
        ApiHelper.get('/api/reports/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch();
    };

    const getPolicies = () => {
        ApiHelper.get(API_URL.MANAGEMENT_POLICY)
            .then((res) => {
                setPoliciesData(res.data.policy_data);
            })
            .catch();
    };

    const getDevicesTypeData = () => {
        setLoading(true);
        // Get selected view statements
        const selectedStatements = viewStatements.filter((statement) => statement.view_id === viewId - 1);

        // Format statements for API call
        const statement = selectedStatements.map((statement, index) => ({
            id: index + 1,
            attribute: statement.deviceattrib,
            negative: statement.negative === 1,
            operator: statement.operator_id,
            value: statement.value,
            connector: index === selectedStatements.length - 1 ? '1' : statement.connector_id
            // operatorOptions: commonData.operators
        }));
        setSelectedStatements(statement);
        ApiHelper.post('/api/reports/devices_type/', {
            group_id: groupId === 0 ? '' : groupId.toString(),
            policy_id: policyId === 0 ? '' : policyId.toString(),
            statement: statement.length > 0 ? statement : []
        })
            .then((res) => {
                setDeviceTypeData(res.data);
                setLoading(false);
                let groupName = 'Any';
                let policyName = 'Any';
                for (let i = 0; i < groupsData.length; i++) {
                    if (groupsData[i].id === groupId) {
                        groupName = groupsData[i].name;
                    }
                }
                for (let i = 0; i < policiesData.length; i++) {
                    if (policiesData[i].id === policyId) {
                        policyName = policiesData[i].name;
                    }
                }
                let viewName = 'Any';
                if (viewId !== 0) {
                    for (let i = 0; i < viewData.length; i++) {
                        if (viewData[i].id === viewId - 1) {
                            viewName = viewData[i].name;
                        }
                    }
                }
                const filterData = [
                    ['Group Name', groupName],
                    ['Policy Name', policyName],
                    ['View Name', viewName]
                ];
                setFilters({
                    group_id: groupId === 0 ? '' : groupId.toString(),
                    policy_id: policyId === 0 ? '' : policyId.toString(),
                    statement: statement.length > 0 ? statement : [],
                    report: 'Device Type (%)',
                    filterData: filterData
                });
            })
            .catch(() => {
                setDeviceTypeData(initialState);
                setLoading(false);
                toast.warning('There is no device data');
            });
    };

    const seriesOfDevicesType = deviceTypeData['type']['values'];
    const optionsOfDevicesType = {
        chart: {
            toolbar: {
                show: false
            },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // this will print mango, apple etc. when clicked on respective datapoint
                    setShowDevicesByType(true);
                    setTypeLabel(deviceTypeData.type.labels[config.dataPointIndex]);
                    setType('type');
                },
                dataPointMouseEnter: (event) => {
                    event.target.style.cursor = 'pointer';
                }
            }
        },
        title: {
            text: 'Devices by Type',
            style: {
                fontSize: '16px',
                fontWeight: '500'
            }
        },
        labels: deviceTypeData.type.labels,
        colors: ['#2F3EB1', '#E38B03', '#A303E3', '#0dcc17ff', '#13e0c5ff'],
        legend: { position: 'bottom' }
    };

    const seriesOfDevicesOS = deviceTypeData['os']['values'];
    const optionsOfDevicesOS = {
        chart: {
            toolbar: {
                show: false
            },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // this will print mango, apple etc. when clicked on respective datapoint
                    setShowDevicesByOs(true);
                    setOSLabel(deviceTypeData.os.labels[config.dataPointIndex]);
                    setType('os');
                },
                dataPointMouseEnter: (event) => {
                    event.target.style.cursor = 'pointer';
                }
            }
        },
        title: {
            text: 'Devices by OS',
            style: {
                fontSize: '16px',
                fontWeight: '500'
            }
        },
        labels: deviceTypeData.os.labels,
        colors: ['#23bbd6ff', '#69e417ff', '#a70448ff', '#6922ecff', '#ec9f0fff'],
        legend: { position: 'bottom' }
    };

    const csvData = [
        ['Devices by OS'],
        deviceTypeData.os.labels,
        deviceTypeData.os.values,
        [''],
        ['Devices by type'],
        deviceTypeData.type.labels,
        deviceTypeData.type.values
    ];

    return (
        <MainCard title="Activity Reports Management" cardHeaderTitle="Devices by Type and OS" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={10}>
                    <Typography display="block" sx={{ fontSize: '16px', maxWidth: 450 }}>
                        Provides a pie chart showing breakdown of device types managed in the past 30 days
                    </Typography>
                </Grid>
                {deviceTypeData.type.values.length > 0 && (
                    <Grid item xs={2}>
                        <ReportFileExports
                            downloadFileName="devices_by_type_and_os_report"
                            downloadFileOption="all"
                            data={csvData}
                            headers={[]}
                            filters={filters}
                            setExportLoading={setExportLoading}
                        />
                    </Grid>
                )}
                <Grid item xs={3}>
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Group</InputLabel>
                                    <Select label="Select Group" value={groupId} name="group" onChange={(e) => setGroupId(e.target.value)}>
                                        <MenuItem value={0}>Any</MenuItem>
                                        {groupsData.length > 0 &&
                                            groupsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Policy</InputLabel>
                                    <Select
                                        label="Select Policy"
                                        value={policyId}
                                        name="policy"
                                        onChange={(e) => setPolicyId(e.target.value)}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {policiesData.length > 0 &&
                                            policiesData.map((data, key) => (
                                                <MenuItem value={data.policyid} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select View</InputLabel>
                                    <Select
                                        label="Select View"
                                        value={viewId}
                                        name="view"
                                        onChange={(e) => setViewId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {viewData.length > 0 &&
                                            viewData.map((data, key) => (
                                                <MenuItem value={data.id + 1} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={getDevicesTypeData}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={9}>
                    <Grid container spacing={gridSpacing} id="deviceType">
                        {loading === true ? (
                            <Grid item xs={12}>
                                <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                    Please wait...
                                </Typography>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={6}>
                                    <ReportVisualization
                                        loading={loading}
                                        data={deviceTypeData.os.values}
                                        type="pie"
                                        options={optionsOfDevicesOS}
                                        series={seriesOfDevicesOS}
                                        height={500}
                                    />
                                    {showDevicesByOs && (
                                        <DeviceTypeDevicesDialog
                                            groupId={groupId}
                                            policyId={policyId}
                                            deviceType={type}
                                            columnIndex={osLabel}
                                            groupsData={groupsData}
                                            policiesData={policiesData}
                                            selectedStatements={selectedStatements}
                                            onClose={() => setShowDevicesByOs(false)}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={6}>
                                    <ReportVisualization
                                        loading={loading}
                                        data={deviceTypeData.type.values}
                                        type="pie"
                                        options={optionsOfDevicesType}
                                        series={seriesOfDevicesType}
                                        height={500}
                                    />
                                    {showDevicesByType && (
                                        <DeviceTypeDevicesDialog
                                            groupId={groupId}
                                            policyId={policyId}
                                            deviceType={type}
                                            columnIndex={typeLabel}
                                            groupsData={groupsData}
                                            policiesData={policiesData}
                                            selectedStatements={selectedStatements}
                                            onClose={() => setShowDevicesByType(false)}
                                        />
                                    )}
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            {exportLoading === true ? (
                <div className="overlayStyle">
                    <div className="spinner spinnerStyle"></div>
                </div>
            ) : null}
        </MainCard>
    );
};

export default ReportsDeviceType;
